import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Package } from 'lucide-react';
import { Button } from '~/components/ui';
import { getInventoryStatus } from '~/utils/inventory';
import type { ReactElement } from 'react';
import type { Inventory } from '~/graphql/graphql';

type InventoryRequirementProps = {
  inventory: Inventory;
  loading?: boolean;
};

function getThresholds(
  urgent: number,
  monitor: number,
  target: number,
  t: (key: string) => string
): ReactElement {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-center">
        <p className="text-xs text-error-600 font-medium">
          {t('inventory.urgent')}
        </p>
        <p className="text-xs text-neut-900">
          &lt; {urgent}
          {t('inventory.weeks_short_form')}
        </p>
      </div>
      <div className="text-center">
        <p className="text-xs text-warning-600 font-medium">
          {t('inventory.monitor')}
        </p>
        <p className="text-xs text-neut-900">
          &lt; {monitor}
          {t('inventory.weeks_short_form')}
        </p>
      </div>
      <div className="text-center">
        <p className="text-xs text-success-600 font-medium">
          {t('inventory.healthy')}
        </p>
        <p className="text-xs text-neut-900">
          &ge; {target}
          {t('inventory.weeks_short_form')}
        </p>
      </div>
    </div>
  );
}

function getWeeksCovered(
  stock: number,
  usageRatePerWeek: number,
  t: (key: string) => string
): string {
  if (usageRatePerWeek === 0) {
    return t('inventory.not_applicable');
  }

  return (stock / usageRatePerWeek).toFixed(1) + ' ' + t('inventory.weeks');
}

export default function InventoryRequirement({
  inventory,
  loading = false,
}: Readonly<InventoryRequirementProps>) {
  const { t } = useTranslation('inventory');
  const stock = inventory?.quantity?.value ?? 0;
  const usageRatePerWeek =
    inventory?.buyerInventoryConfiguration?.usageRatePerWeek?.value ?? 0;
  const targetWeeksCovered =
    inventory?.buyerInventoryConfiguration?.targetWeeksCovered ?? 0;
  const weeksCovered = getWeeksCovered(stock, usageRatePerWeek, t);
  const urgent =
    inventory?.buyerInventoryConfiguration?.weeksCoveredBeforeUrgent ?? 0;
  const monitor =
    inventory?.buyerInventoryConfiguration?.weeksCoveredBeforeMonitor ?? 0;
  const target =
    inventory?.buyerInventoryConfiguration?.targetWeeksCovered ?? 0;
  const navigate = useNavigate();
  const status = getInventoryStatus(
    stock,
    usageRatePerWeek,
    urgent,
    monitor,
    t
  );
  const thresholds = getThresholds(urgent, monitor, target, t);

  return (
    <div className="bg-white rounded-lg border border-neut-200 shadow-sm">
      <div className="px-6 py-4 border-b border-neut-200 bg-neut-50">
        <h2 className="text-sm font-semibold text-neut-900">
          {t('inventory.requirement')}
        </h2>
      </div>

      {!inventory ? (
        <div className="text-center space-y-4">
          <div className="mx-auto w-12 h-12 bg-status-info-50 rounded-full flex items-center justify-center">
            <CircleAlert
              size={24}
              className="text-status-info-600"
              aria-hidden="true"
            />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-neut-900 mb-1">
              {t('inventory.not_set_up')}
            </h3>
            <p className="text-xs text-neut-600 mb-4">
              {t('inventory.not_set_up_description')}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate(`/buyer-inventory`)}
          >
            <Package size={16} className="mr-2" aria-hidden="true" />
            {t('inventory.set_up_now')}
          </Button>
        </div>
      ) : (
        <div className="px-6 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-neut-600">{t('inventory.stock')}</p>
            <p
              className="text-sm font-semibold text-neut-900"
              data-testid="stock-value"
            >
              {loading ? '...' : `${stock} `}
              {t('inventory.mbf')}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-neut-600">{t('inventory.usage')}</p>
            <p className="text-sm font-semibold text-neut-900">
              {loading ? '...' : `${usageRatePerWeek} `}
              {t('inventory.mbf_week')}
            </p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-neut-600">
              {t('inventory.target_cover')}
            </p>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-neut-900">
                {loading
                  ? '...'
                  : targetWeeksCovered + ' ' + t('inventory.weeks')}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-neut-600">{t('inventory.cover')}</p>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-neut-900">
                {loading ? '...' : weeksCovered}
              </p>
              {status}
            </div>
          </div>

          <div className="pt-3 border-t border-neut-200 space-y-2">
            <p className="text-xs font-medium text-neut-700">
              {t('inventory.thresholds')}
            </p>
            {thresholds}
          </div>
        </div>
      )}
    </div>
  );
}
